'use client';

import React from 'react';
import Image from 'next/image';
import { Rocket } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import Link from 'next/link';

const steps = [
  {
    title: 'Analisi completa e pareri utilizzabili',
    description:
      'L’AI non si limita a trovare le norme: le interpreta, collega giurisprudenza e prassi, e formula pareri pronti per essere usati con clienti e colleghi.',
    image: '/images/solutions/analysis.webp',
  },
  {
    title: 'Aggiornamento costante da fonti ufficiali',
    description:
      'Ogni risposta è sempre allineata alle ultime modifiche legislative e giurisprudenziali, con riferimenti diretti a Gazzetta Ufficiale, Agenzia delle Entrate, INPS, INAIL e altre fonti certificate.',
    image: '/images/solutions/sources.webp',
  },
  {
    title: 'Copertura multidisciplinare',
    description:
      'Trovanorma integra ambiti fiscali, legali, del lavoro e di compliance aziendale, garantendo risposte affidabili e aggiornate in ogni settore professionale.',
    image: '/images/solutions/coverage.webp',
  },
];

const SolutionSection = () => {
  return (
    <section id="solution" className="py-16 px-4 md:px-8 bg-primary-50 scroll-mt-12">
      <div className="max-w-xl md:max-w-4xl mx-auto text-center">
        <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6 font-funnel">
          <span className="text-accent">Trovanorma</span> elimina le incertezze e accelera il tuo
          lavoro
        </h2>
        <p className="text-lg text-neutral-700 mb-2">
          Un&apos;unica piattaforma che unisce un database proprietario di fonti ufficiali
          aggiornate ogni giorno con un motore AI specializzato nel diritto e nel fisco italiani.
        </p>
        <p className="text-lg text-neutral-700 mb-12">
          Trovanorma ragiona come un esperto del settore: interpreta, collega e argomenta ogni
          risposta in un formato pronto per essere discusso con clienti, colleghi o in sede legale.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 text-left">
          {steps.map((step, i) => (
            <StepCard
              key={i}
              title={step.title}
              description={step.description}
              image={step.image}
            />
          ))}
        </div>
        <div className="mt-12">
          <Button asChild size="xl" variant="accent-default">
            <Link href={process.env.NEXT_PUBLIC_APP_URL + '/signup'}>
              <Rocket className="w-6 h-6 mr-3" />
              Fai una domanda di prova!
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

interface StepCardProps {
  title: string;
  description: string;
  image: string;
}

const StepCard = ({ title, description, image }: StepCardProps) => {
  return (
    <div className="rounded-xl bg-white shadow-md space-x-4 hover:shadow-lg transition duration-200 border-1 border-neutral-200">
      <Image
        src={image}
        alt={title}
        width={900}
        height={440}
        className="rounded-t-xl object-contain mx-0 my-0"
      />
      <div className="p-6">
        <h3 className="text-xl font-semibold mb-2">{title}</h3>
        <p className="text-gray-600">{description}</p>
      </div>
    </div>
  );
};

export default SolutionSection;
